import {
	DEPTH_COEFFICIENT,
	STEPS_COEFFICIENT,
} from '../../../../../constants/coefficients';
import {
	MAX_STAIR_HEIGHT,
	MIN_STAIR_WIDTH,
} from '../../../../../constants/limits';
import BlockType from '../../../../../enums/blockType';
import EdgeLabel from '../../../../../enums/edgeLabel';
import EdgeType from '../../../../../enums/edgeType';
import { Block, Edge, Segment } from '../../../../../types/shared/blocksV3';
import { getOppositeEdge } from './edgeUtils';
import { v4 as uuidv4 } from 'uuid';
import getVacantSegment from './getVacantSegment';

function getDefaultStair({
	blocks,
	parentBlock,
	selectedBlockEdge,
}: {
	blocks: Block[];
	parentBlock: Block | null;
	selectedBlockEdge: Edge | undefined;
}) {
	if (!parentBlock || !selectedBlockEdge) return;

	const defaultSize = MIN_STAIR_WIDTH;

	const vacantSegment = getVacantSegment({
		blocks,
		parentBlock,
		selectedBlockEdge,
		defaultSize,
	});

	if (!vacantSegment) return;

	const stairWidth = vacantSegment.length;

	const updatedOldSegment: Segment = {
		...vacantSegment,
		length: stairWidth,
		type: EdgeType.STAIR_ATTACHED,
	};

	const newSegment: Segment = {
		id: uuidv4(),
		edgeId: selectedBlockEdge.id,
		blockId: vacantSegment.blockId,
		length: vacantSegment.length - stairWidth,
		type: vacantSegment.type,
	};

	const { defaultStair, addIntermediateLanding } = constructStair({
		parentBlock,
		selectedBlockEdge,
		vacantSegment: updatedOldSegment,
	});

	return {
		defaultStair,
		addIntermediateLanding,
		newSegment,
		updatedOldSegment,
	};
}

export function getDefaultStairForSegment({
	parentBlock,
	selectedBlockEdge,
	selectedBlockSegment,
}: {
	parentBlock: Block | null;
	selectedBlockEdge: Edge | undefined;
	selectedBlockSegment: Segment;
}) {
	if (!parentBlock || !selectedBlockEdge) return;

	const defaultSize = selectedBlockSegment.length;

	if (selectedBlockSegment.length < defaultSize) {
		return;
	}

	const stairWidth = selectedBlockSegment.length;

	const updatedOldSegment: Segment = {
		...selectedBlockSegment,
		length: stairWidth,
		type: EdgeType.STAIR_ATTACHED,
	};

	let newSegment: Segment | null = null;

	if (selectedBlockSegment.length > stairWidth) {
		newSegment = {
			id: uuidv4(),
			edgeId: selectedBlockEdge.id,
			blockId: selectedBlockSegment.blockId,
			length: selectedBlockSegment.length - stairWidth,
			type: selectedBlockSegment.type,
		};
	}

	const { defaultStair, addIntermediateLanding } = constructStair({
		parentBlock,
		selectedBlockEdge,
		vacantSegment: updatedOldSegment,
	});

	return {
		defaultStair,
		addIntermediateLanding,
		updatedOldSegment,
		newSegment,
	};
}

export default getDefaultStair;

export function constructStair({
	parentBlock,
	selectedBlockEdge,
	vacantSegment,
	id: existingId,
	label,
}: {
	parentBlock: Block;
	selectedBlockEdge: Edge;
	vacantSegment: Segment;
	id?: string;
	label?: string;
}): { defaultStair: Block; addIntermediateLanding: boolean } {
	const id = existingId || uuidv4();
	const parentHeight = parentBlock?.dimensions?.height || 0;

	let addIntermediateLanding = false;
	let inferedHeight = parentHeight;

	if (parentHeight > MAX_STAIR_HEIGHT) {
		addIntermediateLanding = true;
		inferedHeight = Math.ceil(parentHeight / 2);
	}

	const inferedWidth = vacantSegment.length;

	const numberOfSteps = Math.ceil(inferedHeight / STEPS_COEFFICIENT);

	const depth = numberOfSteps * DEPTH_COEFFICIENT;

	const endHeight = parentHeight - inferedHeight;

	const defaultStair: Block = {
		id,
		label: label || 'Stair',
		type: BlockType.STAIR,
		// Position will be set in the reducer
		dimensions: {
			width: inferedWidth,
			startHeight: parentHeight,
			height: inferedHeight,
			endHeight,
			numberOfSteps,
			depth,
		}, // Example dimensions
		displayDimensions: {
			width:
				selectedBlockEdge.label == EdgeLabel.TOP ||
				selectedBlockEdge.label == EdgeLabel.BOTTOM
					? inferedWidth
					: depth,
			height:
				selectedBlockEdge.label == EdgeLabel.TOP ||
				selectedBlockEdge.label == EdgeLabel.BOTTOM
					? depth
					: inferedWidth,
		},
		parentBlock: {
			id: parentBlock?.id,
			type: parentBlock?.type,
			edgeId: selectedBlockEdge.id,
			segmentId: vacantSegment.id,
		},
		edges: [
			EdgeLabel.TOP,
			EdgeLabel.RIGHT,
			EdgeLabel.BOTTOM,
			EdgeLabel.LEFT,
		].map((edge) => {
			const edgeId = uuidv4();
			const edgeLength =
				edge == selectedBlockEdge.label ||
				edge == getOppositeEdge(selectedBlockEdge.label)
					? inferedWidth
					: depth;

			const newEdge: Edge = {
				id: edgeId,
				label: edge,
				length: edgeLength,
				attachedToParent: edge == getOppositeEdge(selectedBlockEdge.label),
				segments: [
					{
						id: uuidv4(),
						edgeId,
						blockId: id,
						length: edgeLength,
						type:
							edge === selectedBlockEdge.label
								? endHeight == 0
									? EdgeType.GROUND
									: EdgeType.LANDING_ATTACHED
								: edge === getOppositeEdge(selectedBlockEdge.label)
									? EdgeType.BLOCK_ATTACHED
									: EdgeType.RAILING,
					},
				],
			};

			return newEdge;
		}),
	};

	return { defaultStair, addIntermediateLanding };
}
